import React, { useState } from 'react';
import { ChevronDownOutlined } from '@hangar/react-icons/core/interaction/ChevronDownOutlined';
import { useTranslation } from '../../hooks/i18n';
import { PartializedLoadViewModel } from '../../services/api/flightChanges/types';
import { ItinerarySegment } from './ItinerarySegment';
import * as S from './styles/PartializedLoad.style';

export interface PartializedLoadAccordionProps {
    load: PartializedLoadViewModel;
    defaultExpanded?: boolean;
    variant?: 'previous' | 'new';
}

const PartializedLoadAccordionComponent: React.FC<PartializedLoadAccordionProps> = ({
    load,
    defaultExpanded = false,
    variant = 'new'
}) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(defaultExpanded);

    const piecesLabel = load.piecesCount === 1 
        ? t('itinerary.piece') 
        : t('itinerary.pieces');

    const hasSegments = load.segments && load.segments.length > 0;
    const accordionId = `partial-${load.partNumber}`;
    const summaryId = `${accordionId}-header`;
    const contentId = `${accordionId}-content`;

    return (
        <S.AccordionRoot>
            <S.AccordionHeaderButton
                type="button"
                $expanded={expanded}
                aria-expanded={expanded}
                aria-controls={contentId}
                id={summaryId}
                aria-label={`${t('itinerary.partial')} ${load.partNumber}`}
                onClick={() => setExpanded((v) => !v)}
            >
                <S.AccordionHeaderContent>
                    <S.AccordionTitle>
                        {t('itinerary.partial')} {load.partNumber}
                    </S.AccordionTitle>
                    <S.PiecesBadge variant={variant}>
                        <span>{load.piecesCount}</span>
                        <span>{piecesLabel}</span>
                    </S.PiecesBadge>
                </S.AccordionHeaderContent>
                <S.AccordionChevron $expanded={expanded}>
                    <ChevronDownOutlined aria-hidden />
                </S.AccordionChevron>
            </S.AccordionHeaderButton>
            {expanded && (
                <S.AccordionPanel id={contentId} role="region" aria-labelledby={summaryId}>
                    <S.AccordionContentInner variant={variant}>
                        {hasSegments ? (
                            <S.PartializedSegmentsContainer role="list">
                                {load.segments.map((segment, index) => (
                                    <ItinerarySegment
                                        key={`partial-${load.partNumber}-segment-${segment.leg}-${segment.flightNumber}`}
                                        segment={segment}
                                        variant={variant}
                                        isFirst={index === 0}
                                        isLast={index === load.segments.length - 1}
                                    />
                                ))}
                            </S.PartializedSegmentsContainer>
                        ) : (
                            <S.EmptySegmentsMessage role="status">
                                {t('itinerary.emptyNew')}
                            </S.EmptySegmentsMessage>
                        )}
                    </S.AccordionContentInner>
                </S.AccordionPanel>
            )}
        </S.AccordionRoot>
    );
};

export const PartializedLoadAccordion = React.memo(
    PartializedLoadAccordionComponent,
    (prevProps, nextProps) => {
        return (
            prevProps.load.partNumber === nextProps.load.partNumber &&
            prevProps.load.piecesCount === nextProps.load.piecesCount &&
            prevProps.load.segments?.length === nextProps.load.segments?.length &&
            prevProps.defaultExpanded === nextProps.defaultExpanded &&
            prevProps.variant === nextProps.variant
        );
    }
);
